import { ClientOnly } from "@tanstack/react-router";
import { useContext, useEffect, useRef } from "react";
import { cn } from "tailwind-variants";
import type {
	EmbedUmbBlockElementModel,
	EmbedUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { BlockPreviewContext } from "#/_packages/umbrella/features/previews/block-preview-context";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import {
	tryGetAlignment,
	tryGetReadWidthLength,
} from "#/_packages/umbrella/utils";
import { m } from "#/paraglide/messages";

type EmbedBlockProps = BlockItemProps & {
	content: EmbedUmbBlockElementModel;
	settings: EmbedUmbBlockSettingsElementModel;
};

export default function EmbedUmbBlock({
	className,
	content,
	settings,
}: EmbedBlockProps) {
	const isPreview = useContext(BlockPreviewContext);
	const properties = settings?.properties;
	const alignment = tryGetAlignment(properties?.alignmentHorizontal, "center");
	const embedClassName = cn(
		"block__embed w-full",
		properties?.readWidthToggle && [
			"read-width",
			`read-width-${tryGetReadWidthLength(properties.readWidthLengthPicker)}`,
			`read-width-${alignment === "stretch" ? "center" : alignment}`,
		],
		className,
	);

	if (isPreview) {
		return (
			<div id={properties?.generatedId ?? undefined} className={embedClassName}>
				<div className="flex items-center gap-2 rounded border border-dashed border-border-base bg-surface-muted p-4">
					<span aria-hidden="true">&lt;/&gt;</span>
					<span>{m.embed_preview_label()}</span>
				</div>
			</div>
		);
	}

	if (!content.properties?.embedCode) return null;

	return (
		<ClientOnly>
			<EmbedBlock
				content={content}
				settings={settings}
				className={embedClassName}
			/>
		</ClientOnly>
	);
}

function EmbedBlock({ className, content, settings }: EmbedBlockProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const renderedEmbedCodeRef = useRef<string | null>(null);
	const embedCode = content.properties?.embedCode;

	useEffect(() => {
		const container = containerRef.current;
		if (
			!container ||
			!embedCode ||
			renderedEmbedCodeRef.current === embedCode
		) {
			return;
		}

		container.innerHTML = embedCode;

		for (const inertScript of container.querySelectorAll(
			"script:not([data-umbrella-embed-executed])",
		)) {
			const script = document.createElement("script");

			for (const attribute of inertScript.attributes) {
				script.setAttribute(attribute.name, attribute.value);
			}

			if (!inertScript.hasAttribute("async")) {
				script.async = false;
			}

			script.dataset.umbrellaEmbedExecuted = "";
			script.textContent = inertScript.textContent;
			inertScript.replaceWith(script);
		}

		renderedEmbedCodeRef.current = embedCode;
	}, [embedCode]);

	if (!embedCode) return null;

	return (
		<div
			ref={containerRef}
			id={settings?.properties?.generatedId ?? undefined}
			className={className}
		/>
	);
}
